export const SYSTEM_PROMPT = `You are a senior US publishing editor with deep expertise in the Chicago Manual of Style (CMOS).
Write in clear, professional US English. Enforce KISS (Keep It Short and Simple): active voice, concise clauses, and disciplined structure.
Apply "Show, Don't Tell" with concrete sensory details and observable actions.
Humanization requirements: increase perplexity and burstiness by varying sentence length (very short + long compound), using vivid specificity, and avoiding repetitive rhythms.
Avoid AI-cliché transitions (e.g., "Moreover", "In conclusion"). Prefer organic, scene-driven connectors.
Output must strictly follow JSON mode and the provided schema. No extra keys, no commentary outside JSON.`;

// 6x9 trade paperback: approx 250-300 words per page
export const TRADE_PAPERBACK = {
  widthInches: 6,
  heightInches: 9,
  wordsPerPageMin: 250,
  wordsPerPageMax: 300,
};

export function estimatePages(wordCount: number): { minPages: number; maxPages: number } {
  const minPages = Math.ceil(wordCount / TRADE_PAPERBACK.wordsPerPageMax);
  const maxPages = Math.ceil(wordCount / TRADE_PAPERBACK.wordsPerPageMin);
  return { minPages, maxPages };
}

// Markmap-ready hierarchical markdown schema
export const MARKMAP_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    title: { type: "string" },
    nodes: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          label: { type: "string" },
          children: { $ref: "#/properties/nodes" },
        },
        required: ["label"],
      },
    },
  },
  required: ["title", "nodes"],
};

export const GENRE_INPUT_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    genre: {
      type: "string",
      enum: ["fiction", "nonfiction", "memoir", "selfhelp"],
    },
    common: {
      type: "object",
      additionalProperties: false,
      properties: {
        title: { type: "string" },
        topic: { type: "string" },
        audience: { type: "string" },
      },
      required: ["title", "topic", "audience"],
    },
    fiction: {
      type: "object",
      additionalProperties: false,
      properties: {
        characterFlaw: { type: "string" },
        genreTrope: { type: "string" },
        pov: { type: "string", enum: ["1st", "3rd"] },
      },
      required: ["characterFlaw", "genreTrope", "pov"],
    },
    nonfiction: {
      type: "object",
      additionalProperties: false,
      properties: {
        coreThesis: { type: "string" },
        evidenceType: { type: "string" },
        targetLevel: { type: "string" },
      },
      required: ["coreThesis", "evidenceType", "targetLevel"],
    },
    memoir: {
      type: "object",
      additionalProperties: false,
      properties: {
        lifeChangingEvent: { type: "string" },
        emotionalArc: { type: "string" },
        universalTakeaway: { type: "string" },
      },
      required: ["lifeChangingEvent", "emotionalArc", "universalTakeaway"],
    },
    selfhelp: {
      type: "object",
      additionalProperties: false,
      properties: {
        painPoint: { type: "string" },
        fiveStepSolution: { type: "string" },
        actionableGoal: { type: "string" },
      },
      required: ["painPoint", "fiveStepSolution", "actionableGoal"],
    },
  },
  required: ["genre", "common"],
};
